import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { playerAuthService, LoginPlayerData, RegisterPlayerData, PlayerInfo } from '@/lib/services/player-auth.service';
import { useStore } from './StoreContext';
import { ehLojaDigital } from '@/lib/loja-digital';

interface Player extends Omit<PlayerInfo, 'role'> {
  username: string; // Alias for nick
  avatar: null;
  role: string;
  createdAt: string;
  isOnline: boolean;
}

interface LoginResult {
  success: boolean;
  error?: string;
}

interface AuthContextType {
  user: Player | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;
  openLoginModal: () => void;
  login: (nick: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  register: (email: string, password: string, username: string, name?: string) => Promise<LoginResult>;
  updateCredits: (amount: number) => void;
  refreshUser: () => Promise<void>;
  updateUserEmail: (newEmail: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  /**
   * Opens the sign in somewhere else instead of the modal. A store on a code
   * theme signs in on its own page (`/entrar`), a new document without the theme.
   */
  openLogin?: () => void;
}

/** O jogador salvo no navegador, no formato do contexto. */
export function jogadorSalvo(): Player | null {
  const savedPlayer = playerAuthService.getPlayer();
  if (!savedPlayer) return null;
  return {
    ...savedPlayer,
    username: savedPlayer.nick,
    avatar: null,
    role: (savedPlayer.role as any)?.name || 'Jogador',
    createdAt: new Date().toISOString(),
    isOnline: true,
  };
}

export function AuthProvider({ children, openLogin }: AuthProviderProps) {
  /*
   * Nasce já com o jogador salvo, em vez de preencher num efeito depois do
   * primeiro render. Naquele primeiro render o jogador parecia deslogado, e as
   * páginas que exigem login (`/perfil`, `/parceiro`) redirecionavam para a
   * home quando abertas direto pelo endereço.
   */
  const [user, setUser] = useState<Player | null>(jogadorSalvo);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const { store } = useStore();
  const didBackgroundRefreshRef = useRef(false);

  const openLoginModal = useCallback(() => {
    if (openLogin) openLogin();
    else setShowLoginModal(true);
  }, [openLogin]);

  // Heartbeat to keep the player online
  useEffect(() => {
    if (!user) return;

    if (!store?.id) return;

    playerAuthService.heartbeat(store.id);

    const interval = setInterval(() => {
      playerAuthService.heartbeat(store.id);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback(async (nick: string, password: string): Promise<LoginResult> => {
    if (!store) {
      return { success: false, error: 'Loja não carregada' };
    }

    setIsLoading(true);

    try {
      // Loja de produtos digitais entra pelo e-mail: não tem nick de jogo.
      const authResponse = await playerAuthService.login(
        store.id,
        ehLojaDigital(store) ? { email: nick.trim(), password } : { nick, password },
      );
      // Tokens are now in HttpOnly cookies — only player data is in the response
      playerAuthService.saveAuth(authResponse);

      const playerData: Player = {
        ...authResponse.player,
        username: authResponse.player.nick,
        avatar: null,
        role: authResponse.player.role?.name || 'Jogador',
        createdAt: new Date().toISOString(),
        isOnline: true,
      };

      setUser(playerData);
      setIsLoading(false);
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Nick ou senha inválidos';
      setIsLoading(false);
      return { success: false, error: message };
    }
  }, [store]);

  const logout = useCallback(async () => {
    await playerAuthService.logout(store?.id);
    setUser(null);
  }, [store]);

  const register = useCallback(async (
    email: string,
    password: string,
    username: string,
    name?: string,
  ): Promise<LoginResult> => {
    if (!store) {
      return { success: false, error: 'Loja não carregada' };
    }

    setIsLoading(true);

    try {
      const digital = ehLojaDigital(store);
      await playerAuthService.register(store.id, {
        name: name || username,
        email,
        password,
        // Loja digital: sem nick, a API gera um a partir do e-mail.
        hytaleNick: digital ? undefined : username,
      });

      // Depois do cadastro, entra pelo nick na loja de jogo e pelo e-mail na digital.
      const loginResult = await login(digital ? email : username, password);
      setIsLoading(false);
      return loginResult;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao criar conta. Tente novamente.';
      setIsLoading(false);
      return { success: false, error: message };
    }
  }, [store, login]);

  const updateCredits = useCallback((amount: number) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, credits: prev.credits + amount };
      const saved = playerAuthService.getPlayer();
      playerAuthService.savePlayer({
        id: updated.id,
        storeId: playerAuthService.getStoreId() || '',
        name: updated.name,
        email: updated.email,
        nick: updated.nick,
        credits: updated.credits,
        role: saved?.role ?? null,
      });
      return updated;
    });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!store || !user) return;

    try {
      const response = await playerAuthService.getMyProfile(store.id);
      const updatedPlayer = response.player;

      const updatedUser: Player = {
        id: updatedPlayer.id,
        storeId: store.id,
        name: updatedPlayer.name,
        email: updatedPlayer.email,
        nick: updatedPlayer.nick,
        credits: updatedPlayer.credits,
        role: updatedPlayer.role?.name || 'Player',
        username: updatedPlayer.nick,
        avatar: null,
        createdAt: user.createdAt,
        isOnline: true,
      };

      setUser(updatedUser);
      playerAuthService.savePlayer({
        id: updatedPlayer.id,
        storeId: store.id,
        name: updatedPlayer.name,
        email: updatedPlayer.email,
        nick: updatedPlayer.nick,
        credits: updatedPlayer.credits,
        role: updatedPlayer.role,
      });
    } catch {
      // Silently fail — stale data stays until next login
    }
  }, [store, user]);

  // Background sync after hydration: picks up server-side changes (e.g. role assigned by admin)
  // Runs at most once per session — skipped if user is not logged in or store isn't loaded yet
  useEffect(() => {
    if (!user?.id || !store?.id || didBackgroundRefreshRef.current) return;
    didBackgroundRefreshRef.current = true;
    refreshUser();
  }, [user?.id, store?.id, refreshUser]);

  const updateUserEmail = useCallback((newEmail: string) => {
    if (!user) return;

    const updatedUser = { ...user, email: newEmail };
    setUser(updatedUser);

    const saved = playerAuthService.getPlayer();
    playerAuthService.savePlayer({
      id: user.id,
      storeId: playerAuthService.getStoreId() || '',
      name: user.name,
      email: newEmail,
      nick: user.nick,
      credits: user.credits,
      role: saved?.role ?? null,
    });
  }, [user]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    showLoginModal,
    setShowLoginModal,
    openLoginModal,
    login,
    logout,
    register,
    updateCredits,
    refreshUser,
    updateUserEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Só no editor do construtor: as páginas da conta (perfil, presentes,
 * parceiro) aparecem como ficam com um jogador logado, com este jogador de
 * exemplo. Não há sessão de verdade: nenhuma chamada sai com ele (as rotas do
 * jogador respondem com dados de exemplo, ver `construtor/editor/amostras.ts`)
 * e entrar, sair ou mudar a conta não fazem nada.
 */
export function AuthDeAmostra({ jogador, children }: { jogador: Player; children: ReactNode }) {
  const pai = useContext(AuthContext);
  const value: AuthContextType = {
    user: jogador,
    isLoading: false,
    isAuthenticated: true,
    showLoginModal: false,
    setShowLoginModal: () => {},
    openLoginModal: () => {},
    login: async () => ({ success: false }),
    logout: () => {},
    register: async () => ({ success: false }),
    updateCredits: () => {},
    refreshUser: async () => {},
    updateUserEmail: () => {},
  };
  // Sem o provedor de verdade em volta não há loja montada: não finge nada.
  if (!pai) return <>{children}</>;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export type JogadorDaLoja = Player;

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
